import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { reverseGeocodeLocationToAddress as reverseGeocodeLocationToAddressImpl } from '@rsos/maps/interface/reverseGeocode/helpers';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import type { Coordinates, FoundAddress } from '../../types';

// Type for the reverse geocode function params
interface ReverseGeocodeParams {
  latitude: number;
  longitude: number;
  onReverseGeocoding: React.Dispatch<React.SetStateAction<boolean>>;
  service: string;
  serviceUrl: string | null;
  token: string | null;
}

// Cast the JS function to proper TypeScript types
const reverseGeocodeLocationToAddress =
  reverseGeocodeLocationToAddressImpl as unknown as (
    params: ReverseGeocodeParams,
  ) => Promise<FoundAddress | null>;

interface UseFormReverseGeocodeParams {
  coordinates: Coordinates | null;
  service: string | null;
}

interface UseFormReverseGeocodeReturn {
  isReverseGeocoding: boolean;
  foundAddress: FoundAddress | null;
}

interface GeocodeServiceState {
  geocodeService?: {
    gis_service_url?: string | null;
    token?: string | null;
  };
}

/**
 * Adapted version of useReverseGeocode for form use
 * Returns the reverse geocoded address without storing in Redux
 */
const useFormReverseGeocode = ({
  coordinates,
  service,
}: UseFormReverseGeocodeParams): UseFormReverseGeocodeReturn => {
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [foundAddress, setFoundAddress] = useState<FoundAddress | null>(null);

  const gisGeocodeService = useSelector(selectGeocodeService) as
    | GeocodeServiceState['geocodeService']
    | null;

  const gisGeocodeServiceUrl = gisGeocodeService?.gis_service_url || null;
  const gisGeocodeServiceToken = gisGeocodeService?.token || null;

  // Reverse geocode coordinates to address
  useEffect(() => {
    if (!coordinates?.latitude || !coordinates?.longitude || !service) {
      return;
    }

    const location = {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      onReverseGeocoding: setIsReverseGeocoding,
      service,
      serviceUrl: gisGeocodeServiceUrl,
      token: gisGeocodeServiceToken,
    };

    reverseGeocodeLocationToAddress(location)
      .then((response: FoundAddress | null) => {
        if (response) {
          setFoundAddress(response);
        } else {
          setFoundAddress(null);
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Reverse geocoding failed:', error);
        setFoundAddress(null);
      });
  }, [coordinates, service, gisGeocodeServiceUrl, gisGeocodeServiceToken]);

  return {
    isReverseGeocoding,
    foundAddress,
  };
};

export default useFormReverseGeocode;
